import LoadingButton from "@/components/common/form/loading-button";
import Modal from "@/components/common/ui/modal";
import SiteIcon from "@/components/common/ui/site-icon";
import SiteTypeName from "@/components/site/site-type-name";
import { UPDATE_ORGANIZATION } from "@/gql/organization";
import { CREATE_SITE, GET_SITE_META_INFO } from "@/gql/site";
import { IOrganization } from "@/types/organization";
import { selectTeam } from "@/utils/common";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  ICreateSiteMutation,
  ICreateSiteMutationVariables,
  IGetSiteMetaInfoQuery,
  IGetSiteMetaInfoQueryVariables,
  IUpdateOrganizationMutation,
  IUpdateOrganizationMutationVariables,
} from "@shared/common/types/api-private/generated/graphql";
import { ISite } from "@shared/common/types/site";
import { Dropdown, Form, Input } from "antd";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";
import FormLabel from "../auth/form-label";
import OnboardingGuide from "./onboarding/onboarding-guide";
import OnboardingLoading from "./onboarding/onboarding-loading";
import OnboardingMessage from "./onboarding/onboarding-message";
import OnboardingScript from "./onboarding/onboarding-script";

interface IOnboardingModalProps {
  organizations: IOrganization[];
  currentSite: ISite | null;
  currentOrganization: IOrganization;
  reload: () => Promise<unknown>;
  onComplete: () => Promise<void>;
  showOnboarding: boolean;
}

interface ISiteFormValues {
  url: string;
  name: string;
}

const v2UrlPrefix = process.env.NEXT_PUBLIC_V2_URL_PREFIX;

const OnboardingModal: React.FC<IOnboardingModalProps> = ({
  organizations,
  currentOrganization,
  currentSite,
  reload,
  onComplete,
  showOnboarding,
}) => {
  const router = useRouter();

  const [open, setOpen] = useState(showOnboarding);
  const [step, setStep] = useState(
    currentOrganization.onboardStep === 30
      ? 3
      : currentOrganization.onboardStep === 40
      ? 4
      : 1
  );

  const [createLoading, setCreateLoading] = useState(false);

  const [siteMetaInfo, setSiteMetaInfo] =
    useState<IGetSiteMetaInfoQuery["siteMetaInfo"]>();

  const [form] = Form.useForm<ISiteFormValues>();

  const [getSiteMetaInfo] = useLazyQuery<
    IGetSiteMetaInfoQuery,
    IGetSiteMetaInfoQueryVariables
  >(GET_SITE_META_INFO);

  const [createSite] = useMutation<
    ICreateSiteMutation,
    ICreateSiteMutationVariables
  >(CREATE_SITE, {
    onError: (err) => {
      toast(
        err.message ||
          "사이트를 추가하지 못했습니다. 잠시 후에 다시 시도해주세요."
      );
    },
  });

  const [updateTeam] = useMutation<
    IUpdateOrganizationMutation,
    IUpdateOrganizationMutationVariables
  >(UPDATE_ORGANIZATION);

  useEffect(() => {
    setOpen(showOnboarding);
  }, [currentOrganization, showOnboarding]);

  const handleFormFinish = useCallback(
    async (val: ISiteFormValues) => {
      if (step === 1) {
        setStep(1.5);

        const ret = await Promise.all([
          new Promise((resolve) => setTimeout(resolve, 2500)),
          (async () => {
            const { data, error } = await getSiteMetaInfo({
              variables: { url: val.url },
            });
            return { data, error };
          })(),
        ]);

        setSiteMetaInfo(ret[1].data?.siteMetaInfo);
        form.setFieldValue(
          "name",
          ret[1].data?.siteMetaInfo?.name || "내 사이트"
        );

        setTimeout(() => form.getFieldInstance("url")?.blur(), 1);

        setStep(2);
      } else if (step === 2) {
        setCreateLoading(true);

        try {
          const ret = await createSite({
            variables: {
              organizationId: currentOrganization.id,
              object: val,
            },
          });

          if (ret.data?.createSite) {
            // step 변경
            await updateTeam({
              variables: {
                organizationId: currentOrganization.id,
                object: { onboardStep: 30 },
              },
            });

            await reload();
            setStep(3);
          }
        } finally {
          setCreateLoading(false);
        }
      }
    },
    [
      step,
      form,
      getSiteMetaInfo,
      createSite,
      currentOrganization.id,
      updateTeam,
      reload,
    ]
  );

  const handleClose = useCallback(async () => {
    await onComplete();
    setOpen(false);
  }, [onComplete]);

  const handleOrganizationClick = useCallback(
    (organization: IOrganization) => {
      selectTeam(organization.id);
      setOpen(false);
      router.push(`${v2UrlPrefix}/${organization.slug}`);
    },
    [router]
  );

  const organizationItems = useMemo(() => {
    return organizations
      .filter((org) => org.id != currentOrganization.id)
      .map((org) => ({
        key: org.id,
        label: org.name,
        onClick: () => handleOrganizationClick(org),
      }));
  }, [currentOrganization.id, handleOrganizationClick, organizations]);

  return (
    <Modal
      open={open}
      title={
        step === 1 || step === 2
          ? "사이트 추가"
          : step === 3
          ? "공통 스크립트 설치"
          : "코드앤버터를 사용할 준비가 끝났어요!"
      }
      headerRight={
        <span className="px-2 py-0.5 text-xs rounded-full bg-cb-gray-100 text-cb-gray-800">
          {step}/4
        </span>
      }
      backdrop
      hideCloseButton
      hideFooter
    >
      {step === 1 || step === 1.5 || step === 2 ? (
        <>
          <Form
            form={form}
            requiredMark={false}
            layout="vertical"
            initialValues={{ name: "", url: "" }}
            onFinish={handleFormFinish}
          >
            <div className="text-center text-cb-gray-700">
              {step === 1 || step === 1.5 ? (
                <>
                  <OnboardingMessage message="거의 다 왔어요. 몇 가지만 더 해볼까요~?" />

                  <div className="mt-4">
                    코드앤버터를 이용하기 위해 사이트 정보를 정확하게 입력해
                    주세요.
                  </div>
                </>
              ) : (
                <></>
              )}

              {step === 2 ? (
                <>
                  {siteMetaInfo?.siteType ? (
                    <OnboardingMessage
                      message={
                        <>
                          사이트 종류는 &quot;
                          <SiteTypeName siteType={siteMetaInfo.siteType} />
                          &quot; 입니다.
                        </>
                      }
                    />
                  ) : (
                    <></>
                  )}

                  <div className="w-16 h-16 mx-auto mt-4 center-center">
                    <SiteIcon
                      name={siteMetaInfo?.name || "내 사이트"}
                      icon={siteMetaInfo?.icon}
                      className="w-16 h-16"
                    />
                  </div>
                </>
              ) : (
                <></>
              )}

              <div className="mt-8 text-left w-104">
                <Form.Item
                  name="name"
                  label={<FormLabel label="사이트명" required={step === 2} />}
                  className={twMerge(
                    "mb-6",
                    (step === 1 || step === 1.5) && "hidden"
                  )}
                  rules={[
                    {
                      required: step === 2,
                      message: "사이트명을 입력해주세요.",
                    },
                  ]}
                >
                  <Input
                    placeholder="사이트명을 입력해주세요."
                    className="w-full text-base text-black"
                  />
                </Form.Item>

                <Form.Item
                  name="url"
                  label={<FormLabel label="도메인" required />}
                  rules={[
                    { required: true, message: "도메인을 입력해주세요." },
                  ]}
                >
                  <Input
                    placeholder="도메인을 입력해주세요."
                    className="w-full text-base text-black"
                  />
                </Form.Item>

                <LoadingButton
                  type="submit"
                  theme="primary"
                  className="mt-8"
                  block
                  loading={step === 2 && createLoading}
                  data-gtm-id={
                    step === 1
                      ? "onboarding_site_add"
                      : "onboarding_site_add_confirm"
                  }
                >
                  추가
                </LoadingButton>
                {step === 1 && organizations.length > 1 ? (
                  <div className="flex mt-1 center-center">
                    <Dropdown
                      menu={{
                        items: organizationItems,
                      }}
                      trigger={["click"]}
                      className="text-sm"
                    >
                      <a
                        className="mx-auto h-[34px] leading-[34px] text-cb-gray-500 hover:text-cb-gray-900"
                        onClick={(e) => e.preventDefault()}
                      >
                        다른 팀 선택하기
                      </a>
                    </Dropdown>
                  </div>
                ) : (
                  <></>
                )}
                {step === 2 ? (
                  <LoadingButton
                    theme="text"
                    className="mt-1"
                    block
                    size="small"
                    onClick={() => {
                      form.resetFields();
                      setStep(1);
                      setTimeout(
                        () => form.getFieldInstance("url")?.focus(),
                        1
                      );
                    }}
                    data-gtm-id="onboarding_site_add_retry"
                  >
                    다시 입력하기
                  </LoadingButton>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </Form>

          {step === 1.5 ? <OnboardingLoading /> : <></>}
        </>
      ) : (
        <></>
      )}

      {step === 3 ? (
        <OnboardingScript
          currentOrganization={currentOrganization}
          publicId={currentSite?.publicId || ""}
          siteType={currentSite?.siteType}
          onChangeStep={setStep}
        />
      ) : (
        <></>
      )}

      {step === 4 ? (
        <OnboardingGuide
          currentOrganization={currentOrganization}
          setStep={setStep}
          onClose={handleClose}
          previewStep={3}
        />
      ) : (
        <></>
      )}
    </Modal>
  );
};

export default OnboardingModal;
